import { Link } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  GraduationCap, Zap, Building2, Target, Users, Globe, MessageSquare,
  ArrowRight, Mail, ShieldCheck, BarChart3, FlaskConical, Wallet, Sparkles,
  Cloud, Fingerprint, LineChart, Bell, CreditCard, Network, Compass,
  Workflow, Code2, Boxes, Activity, Layers, Rocket, Heart, Hammer, Lock,
  TrendingUp, Infinity as InfinityIcon,
} from "lucide-react";

// ---------- Products (business-outcome focused, no AI-first wording) ----------
const PRODUCTS = [
  {
    icon: GraduationCap, name: "LaneX Academy", tag: "Learning Platform",
    body: "A personalised learning platform that helps learners develop practical skills through adaptive learning, guided coaching, coding practice, projects and certifications.",
    href: "https://lanexacademy.com", cta: "Visit Academy",
  },
  {
    icon: Zap, name: "SyncLyn", tag: "Business Operations",
    body: "A business operations platform bringing communication, recruitment, customer engagement and workflow automation into one connected workspace.",
    href: "https://synclyntech.com", cta: "Explore SyncLyn",
  },
  {
    icon: BarChart3, name: "Quixia", tag: "Marketing Intelligence",
    body: "A marketing intelligence platform helping businesses understand audiences, manage campaigns and make smarter marketing decisions from unified data.",
    href: "/holdings/quixia", cta: "Learn more",
  },
  {
    icon: ShieldCheck, name: "Vaultxy", tag: "Cybersecurity",
    body: "A cybersecurity platform protecting modern organisations through continuous monitoring, intelligent detection and clear, actionable security insights.",
    href: "/holdings/vaultxy", cta: "Learn more",
  },
  {
    icon: FlaskConical, name: "LaneX Labs", tag: "Innovation Studio",
    body: "The innovation studio where new software products are researched, designed, validated and launched — the source of everything we ship next.",
    href: "/holdings/labs", cta: "Learn more",
  },
  {
    icon: Wallet, name: "FlexIt", tag: "Financial Technology",
    body: "A financial technology platform helping individuals and businesses manage money, move payments and plan their finances with clarity and control.",
    href: "/holdings/flexit", cta: "Learn more",
  },
];

// ---------- Shared Engineering Platform ----------
const CAPABILITIES = [
  { icon: Cloud,        title: "Cloud Infrastructure",        body: "Isolated, region-aware infrastructure engineered for uptime, resilience and global scale." },
  { icon: Fingerprint,  title: "Identity & Access",           body: "One identity across every product — SSO, RBAC and enterprise access controls out of the box." },
  { icon: Lock,         title: "Security & Compliance",       body: "Encryption in transit and at rest, audit logging, and compliance-ready processes as a default." },
  { icon: LineChart,    title: "Analytics",                   body: "A shared analytics pipeline so every team sees the same numbers in real time." },
  { icon: Workflow,     title: "Workflow Automation",         body: "Reusable automation primitives that power flows across every LaneX product." },
  { icon: CreditCard,   title: "Billing",                     body: "Subscriptions, usage pricing, invoicing and enterprise contracts on one billing engine." },
  { icon: MessageSquare,title: "Communications",              body: "Email, in-app, chat and messaging channels wired into a single delivery layer." },
  { icon: Code2,        title: "Developer Platform",          body: "Shared SDKs, tooling and internal APIs that let teams ship consistent products faster." },
  { icon: Network,      title: "Integrations",                body: "Native connectors to the tools our customers already use — CRMs, comms, storage and more." },
  { icon: Sparkles,     title: "Intelligent Assistance",      body: "Adaptive, in-product assistance that helps users act faster with clearer guidance." },
  { icon: Boxes,        title: "API Platform",                body: "Public APIs and webhooks so customers can extend our products and build on top of them." },
  { icon: Activity,     title: "Observability",               body: "Structured logging, tracing and metrics that keep every product measurably healthy." },
  { icon: Bell,         title: "Notifications",               body: "Cross-channel notification delivery with per-user preferences and quiet hours." },
  { icon: Layers,       title: "Shared Services",             body: "Common services — user profiles, files, tenants — reused across the entire ecosystem." },
];

// ---------- Metrics (numeric ones animate) ----------
const METRICS = [
  { value: 6,    suffix: "",   label: "Products",           note: "Live and in development" },
  { value: 1,    suffix: "",   label: "Technology Ecosystem", note: "One shared platform" },
  { value: 100,  suffix: "%",  label: "Cloud Native",       note: "Designed for global scale" },
  { value: null, text: "Global", label: "Built for Global Scale", note: "Distributed by design" },
  { value: null, text: "Monthly", label: "Growing Every Month", note: "Continuous product releases" },
  { value: null, text: "UK",   label: "Headquartered",      note: "United Kingdom" },
];

// ---------- Why LaneX ----------
const WHY = [
  { icon: Target,     title: "Technology with Purpose", body: "Everything begins with solving a genuine problem. If it doesn't help a real person or team, we don't build it." },
  { icon: Hammer,     title: "Engineering Excellence",  body: "Quality software built to last — clean architecture, tested code, and long-term craftsmanship over shortcuts." },
  { icon: Heart,      title: "Customer First",          body: "Products evolve through real customer feedback. Our roadmap is written by the people who use what we build." },
  { icon: ShieldCheck,title: "Secure by Design",        body: "Security is built in from day one — never bolted on. Every product ships on the same hardened foundation." },
  { icon: TrendingUp, title: "Scalable Architecture",   body: "Every product is designed to grow — from the first user to the millionth — on infrastructure that scales with them." },
  { icon: InfinityIcon, title: "Long-term Vision",      body: "We build companies measured in decades, not trends. Patient engineering, patient business, patient growth." },
];

// ---------- Leadership philosophy ----------
const LEADERSHIP = [
  "Small teams.",
  "Fast execution.",
  "Customer obsession.",
  "Continuous innovation.",
  "Responsible technology.",
  "Global thinking.",
];

// ---------- Small counter hook (numeric metrics only) ----------
function useCountUp(target, duration = 1200) {
  const [value, setValue] = useState(0);
  const ref = useRef(null);
  const started = useRef(false);
  useEffect(() => {
    if (target == null) return;
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && !started.current) {
          started.current = true;
          const startTs = performance.now();
          const step = (ts) => {
            const p = Math.min((ts - startTs) / duration, 1);
            const eased = 1 - Math.pow(1 - p, 3);
            setValue(Math.round(target * eased));
            if (p < 1) requestAnimationFrame(step);
          };
          requestAnimationFrame(step);
        }
      });
    }, { threshold: 0.4 });
    io.observe(el);
    return () => io.disconnect();
  }, [target, duration]);
  return [value, ref];
}

function MetricCard({ metric }) {
  const [count, ref] = useCountUp(metric.value);
  const numeric = metric.value != null;
  return (
    <div
      ref={ref}
      className="rounded-2xl border border-border bg-card p-6 transition hover:border-primary/60"
      data-testid={`about-metric-${metric.label.toLowerCase().replace(/\s+/g, "-")}`}
    >
      <div className="font-display text-5xl font-black tracking-tighter text-primary leading-none">
        {numeric ? `${count}${metric.suffix || ""}` : metric.text}
      </div>
      <div className="mt-3 font-display text-base font-bold tracking-tight">{metric.label}</div>
      <div className="mt-1 font-mono text-[0.65rem] uppercase tracking-widest text-muted-foreground">{metric.note}</div>
    </div>
  );
}

export default function AboutPage() {
  return (
    <div className="min-h-screen bg-background" data-testid="about-page">
      {/* Header */}
      <header className="border-b border-border sticky top-0 z-40 bg-background/80 backdrop-blur">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-4">
          <Link to="/" data-testid="about-logo-link" className="inline-flex items-center gap-2">
            <img src="/lanex-mark.png" alt="LaneX" className="h-9 w-auto object-contain bg-white/95 rounded-lg p-1 shadow-md ring-1 ring-primary/30 drop-shadow-[0_0_12px_rgba(0,238,199,0.45)]" />
            <span className="font-display text-sm font-bold tracking-tight">LaneX Holdings</span>
          </Link>
          <nav className="flex flex-wrap items-center gap-4 text-sm md:gap-5">
            <a href="#products"   className="text-muted-foreground hover:text-foreground transition">Products</a>
            <a href="#technology" className="hidden md:inline text-muted-foreground hover:text-foreground transition">Technology</a>
            <a href="#why"        className="hidden md:inline text-muted-foreground hover:text-foreground transition">Why LaneX</a>
            <a href="#vision"     className="hidden md:inline text-muted-foreground hover:text-foreground transition">Vision</a>
            <a href="mailto:hello@lanexacademy.com" className="text-muted-foreground hover:text-foreground transition">Contact</a>
          </nav>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-border">
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute -top-24 -left-24 h-96 w-96 rounded-full bg-primary/10 blur-3xl" />
          <div className="absolute bottom-0 right-0 h-96 w-96 rounded-full bg-primary/5 blur-3xl" />
        </div>
        <div className="relative mx-auto max-w-6xl px-4 pt-16 pb-16 md:pt-24 md:pb-20">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 rounded-full border border-primary/40 bg-primary/5 px-3 py-1 font-mono text-[0.65rem] uppercase tracking-widest text-primary">
              <Building2 className="h-3 w-3" /> lanex holdings
            </div>
            <h1 className="mt-6 font-display text-5xl font-black tracking-tighter md:text-6xl leading-[1.05]">
              Building <span className="text-primary">software people rely on.</span>
            </h1>
            <p className="mt-6 text-lg text-muted-foreground leading-relaxed">
              LaneX Holdings develops modern software platforms that help individuals and organisations learn faster, work smarter, communicate better and grow confidently. Every product is built on shared engineering, security and intelligent technologies that scale together.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button asChild size="lg" className="rounded-full btn-neon font-mono text-xs uppercase tracking-widest" data-testid="about-cta-products">
                <a href="#products"><Compass className="mr-2 h-4 w-4" /> Explore Products</a>
              </Button>
              <Button asChild size="lg" variant="outline" className="rounded-full font-mono text-xs uppercase tracking-widest">
                <a href="#why"><Building2 className="mr-2 h-4 w-4" /> Learn About LaneX</a>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Products */}
      <section id="products" className="mx-auto max-w-6xl px-4 py-20">
        <div className="font-mono text-[0.7rem] uppercase tracking-widest text-primary">// our products</div>
        <h2 className="mt-2 font-display text-4xl font-black tracking-tighter md:text-5xl">Six platforms. One company.</h2>
        <p className="mt-3 max-w-2xl text-sm text-muted-foreground leading-relaxed">
          Each LaneX product is a standalone platform solving a distinct problem — from learning and operations to security, marketing and finance.
        </p>

        <div className="mt-10 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {PRODUCTS.map((p) => {
            const isLink = p.href && p.href !== "#";
            const isExternal = isLink && /^https?:\/\//i.test(p.href);
            const Wrapper = !isLink ? "div" : isExternal ? "a" : Link;
            const wrapperProps = !isLink
              ? {}
              : isExternal
                ? { href: p.href, target: "_blank", rel: "noreferrer" }
                : { to: p.href };
            return (
              <Wrapper
                key={p.name}
                {...wrapperProps}
                data-testid={`about-product-${p.name.toLowerCase().replace(/\s+/g, "-")}`}
                className={`group relative flex flex-col rounded-2xl border border-border bg-card p-6 transition ${isLink ? "hover:border-primary hover:shadow-[0_0_32px_-8px_rgba(0,229,255,0.35)] cursor-pointer" : ""}`}
              >
                <div className="flex items-center justify-between">
                  <div className="grid h-11 w-11 place-items-center rounded-xl border border-primary/40 bg-primary/10 text-primary">
                    <p.icon className="h-5 w-5" />
                  </div>
                  <div className="font-mono text-[0.65rem] uppercase tracking-widest text-muted-foreground">{p.tag}</div>
                </div>
                <div className="mt-5">
                  <h3 className="font-display text-2xl font-black tracking-tighter">{p.name}</h3>
                  <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{p.body}</p>
                </div>
                <div className="mt-5 flex items-center gap-1 font-mono text-[0.7rem] uppercase tracking-widest text-primary">
                  {p.cta} <ArrowRight className={`h-3.5 w-3.5 transition ${isLink ? "group-hover:translate-x-1" : ""}`} />
                </div>
              </Wrapper>
            );
          })}
        </div>
      </section>

      {/* Shared engineering platform */}
      <section id="technology" className="border-t border-border bg-card/30">
        <div className="mx-auto max-w-6xl px-4 py-20">
          <div className="font-mono text-[0.7rem] uppercase tracking-widest text-primary">// shared engineering platform</div>
          <h2 className="mt-2 font-display text-4xl font-black tracking-tighter md:text-5xl">Built on one technology foundation.</h2>
          <p className="mt-3 max-w-2xl text-sm text-muted-foreground leading-relaxed">
            Every product ships on the same shared platform — so security, reliability, performance and intelligent assistance aren't reinvented for each product. They're a company-wide default.
          </p>

          <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {CAPABILITIES.map((t) => (
              <div key={t.title} className="rounded-2xl border border-border bg-card p-6 transition hover:border-primary/60">
                <div className="grid h-10 w-10 place-items-center rounded-xl border border-primary/40 bg-primary/10 text-primary">
                  <t.icon className="h-4 w-4" />
                </div>
                <h3 className="mt-4 font-display text-lg font-bold tracking-tight">{t.title}</h3>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{t.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Metrics */}
      <section className="mx-auto max-w-6xl px-4 py-20">
        <div className="font-mono text-[0.7rem] uppercase tracking-widest text-primary">// by the numbers</div>
        <h2 className="mt-2 font-display text-4xl font-black tracking-tighter md:text-5xl">A company, not a product.</h2>
        <p className="mt-3 max-w-2xl text-sm text-muted-foreground leading-relaxed">
          A snapshot of where LaneX Holdings stands today.
        </p>
        <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {METRICS.map((m) => (<MetricCard key={m.label} metric={m} />))}
        </div>
      </section>

      {/* Why LaneX */}
      <section id="why" className="border-t border-border bg-gradient-to-b from-primary/[0.04] to-transparent">
        <div className="mx-auto max-w-6xl px-4 py-20">
          <div className="font-mono text-[0.7rem] uppercase tracking-widest text-primary">// why lanex</div>
          <h2 className="mt-2 font-display text-4xl font-black tracking-tighter md:text-5xl">Six beliefs behind every product.</h2>
          <p className="mt-3 max-w-2xl text-sm text-muted-foreground leading-relaxed">
            These aren't slogans. They're the operating principles behind how we design, build and run every part of the LaneX ecosystem.
          </p>
          <div className="mt-10 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {WHY.map((v) => (
              <div key={v.title} className="rounded-2xl border border-border bg-card p-6 transition hover:border-primary/60">
                <div className="grid h-10 w-10 place-items-center rounded-xl border border-primary/40 bg-primary/10 text-primary">
                  <v.icon className="h-4 w-4" />
                </div>
                <h3 className="mt-4 font-display text-lg font-bold tracking-tight">{v.title}</h3>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{v.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Leadership philosophy */}
      <section className="mx-auto max-w-6xl px-4 py-20">
        <div className="grid gap-10 md:grid-cols-[1fr_1.2fr] md:items-start">
          <div>
            <div className="font-mono text-[0.7rem] uppercase tracking-widest text-primary">// how we lead</div>
            <h2 className="mt-2 font-display text-4xl font-black tracking-tighter md:text-5xl">Leadership philosophy.</h2>
            <p className="mt-3 text-sm text-muted-foreground leading-relaxed">
              The way we run the company matters as much as what we build. Six ideas guide every hire, every decision and every product we ship.
            </p>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            {LEADERSHIP.map((line, i) => (
              <div key={line} className="rounded-2xl border border-border bg-card p-5 transition hover:border-primary/60">
                <div className="font-mono text-[0.6rem] uppercase tracking-widest text-primary">{String(i + 1).padStart(2, "0")}</div>
                <div className="mt-2 font-display text-lg font-bold tracking-tight">{line}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Future Vision */}
      <section id="vision" className="border-t border-border">
        <div className="mx-auto max-w-6xl px-4 py-24">
          <div className="mx-auto max-w-4xl text-center">
            <div className="font-mono text-[0.7rem] uppercase tracking-widest text-primary">// vision</div>
            <h2 className="mt-4 font-display text-4xl font-black tracking-tighter md:text-6xl leading-[1.05]">
              We&rsquo;re not building six products.
            </h2>
            <h2 className="mt-3 font-display text-4xl font-black tracking-tighter md:text-6xl leading-[1.05]">
              We&rsquo;re building the <span className="text-primary">software ecosystem</span> that helps people learn, work and grow throughout their lives.
            </h2>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="mx-auto max-w-6xl px-4 py-20">
        <div className="rounded-3xl border border-primary/30 bg-gradient-to-br from-primary/10 to-transparent p-8 md:p-12">
          <div className="grid gap-6 md:grid-cols-2 md:items-center">
            <div>
              <div className="font-mono text-[0.7rem] uppercase tracking-widest text-primary">// let&rsquo;s talk</div>
              <h2 className="mt-2 font-display text-3xl font-black tracking-tighter md:text-4xl">Partnerships, press, investors or just a hello.</h2>
              <p className="mt-3 text-sm text-muted-foreground">
                We&rsquo;re a small, focused team and we read every message. Reach out about enterprise pilots, licensing, careers, or to say hi.
              </p>
            </div>
            <div className="flex flex-col gap-3">
              <Button asChild size="lg" className="rounded-full btn-neon font-mono text-xs uppercase tracking-widest" data-testid="about-cta-email">
                <a href="mailto:hello@lanexacademy.com"><Mail className="mr-2 h-4 w-4" /> hello@lanexacademy.com</a>
              </Button>
              <Button asChild size="lg" variant="outline" className="rounded-full font-mono text-xs uppercase tracking-widest">
                <a href="https://synclyntech.com" target="_blank" rel="noreferrer"><MessageSquare className="mr-2 h-4 w-4" /> Explore SyncLyn</a>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border py-10">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-4 px-4">
          <div className="font-mono text-xs text-muted-foreground">© {new Date().getFullYear()} LaneX Holdings. All rights reserved.</div>
          <div className="flex items-center gap-4 font-mono text-xs">
            <Link to="/" className="text-muted-foreground hover:text-foreground transition">LaneX Academy</Link>
            <a href="https://synclyntech.com" target="_blank" rel="noreferrer" className="text-muted-foreground hover:text-foreground transition">SyncLyn</a>
            <a href="mailto:hello@lanexacademy.com" className="text-muted-foreground hover:text-foreground transition">Contact</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
